<script setup lang="ts">
import {
  usePageConnectorStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { ChangeHotelEventSource } from '@apps/main/new-desktop/core/models';
import type { ChangeHotelEventMetadata } from '@apps/main/new-desktop/core/models';
import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';

import { AvailabilityOrder } from '@libs/trip-planner-interfaces';

import type { ItineraryAccommodationSegment } from '../../../models';
import {
  isAlternativeRoomSuggestionAccommodationSegment,
  isAvailableAccommodationSegment,
  isUnavailableAccommodationSegment,
} from '../../../models';
import {
  useTripOverviewPanelsStore,
  useTripOverviewPageStore,
} from '../../../stores';

import AvailableAccommodationSegment from './AvailableAccommodationSegment.vue';
import AlternativeRoomSuggestionAccommodationSegment from './AlternativeRoomSuggestionAccommodationSegment.vue';
import UnavailableAccommodationSegment from './UnavailableAccommodationSegment.vue';
// NOTE: Important:
// * this component is smart
// * keep child components of this one dumb
// * every action in this component must be guarded by print mode flag

interface ItineraryAccommodationSegmentProps {
  segment: ItineraryAccommodationSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryAccommodationSegmentProps>();

const pageConnectorStore = usePageConnectorStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const filtersStore = useFiltersStore();
const recipeStore = useRecipeStore();
const editTripStore = useEditTripStore();

function onShowDetails(): void {
  if (props.printMode) {
    return;
  }

  if (!props.segment.productCardData) {
    throw new Error(
      'Expected productCardData when showing product card segment!'
    );
  }

  if (props.segment.context.hotelCode) {
    tripOverviewPanelsStore.openHotelDetailsPanel(
      props.segment.context.hotelCode,
      props.segment.context.ingredientId
    );
  }
}

function onShowRooms(isAlternativeRoom: boolean): void {
  if (props.printMode) {
    return;
  }

  if (!props.segment.productCardData) {
    throw new Error(
      'Expected productCardData when showing product card segment!'
    );
  }

  if (isAlternativeRoom) {
    statsigEventsStore.logTripOverviewSeeAlternativeRoomsWhenNoAvail();
  }

  if (props.segment.context.hotelCode) {
    if (!isAlternativeRoom) {
      statsigEventsStore.logTripOverviewEditRoomClicked();
    }

    tripOverviewPanelsStore.openHotelDetailsPanel(
      props.segment.context.hotelCode,
      props.segment.context.ingredientId,
      true
    );
  }
}

function confirmAlternative(): void {
  if (isAlternativeRoomSuggestionAccommodationSegment(props.segment)) {
    tripOverviewPageStore.confirmAlternativeRoomSelection(props.segment);
  }
}

function onHotelSelectionNavigationTriggered(
  source: ChangeHotelEventSource
): void {
  if (props.printMode) {
    return;
  }
  const metaData: ChangeHotelEventMetadata = { source };
  statsigEventsStore.logTripOverviewHotelChangeClicked(metaData);
}

function onShowFilterPanel(source: 'from-plus' | 'from-more'): void {
  if (props.printMode) {
    return;
  }

  statsigEventsStore.logTripOverviewPreferencesModalOpened({ source });
  tripOverviewPanelsStore.openHotelFiltersPanel(
    props.segment.context.ingredientId
  );
}

function onRemoveFilter(id: HotelFilter): void {
  if (props.printMode) {
    return;
  }
  statsigEventsStore.logTripOverviewFiltersRemoved();
  filtersStore.removeFilterFromTripOverview(
    id,
    props.segment.context.ingredientId
  );
}

//NOTE: mix of onUpdateSuggestionType() and onToggleAccommodation() -> will need refactor to mix both logics
function onToggleAccommodation(
  selectedOption: AccommodationSelectorType
): void {
  if (props.printMode) {
    return;
  }
  if (selectedOption === 'noAccommodation') {
    const isDisabled = false;
    statsigEventsStore.logTripOverviewHotelToggle(isDisabled);

    editTripStore.removeAccommodationFromHotelIngredient(
      props.segment.context.ingredientId
    );
  } else {
    const ingredient = recipeStore.getHotelIngredient(
      props.segment.context.ingredientId
    );
    const availabilityOrder =
      selectedOption === 'popularAccommodation'
        ? AvailabilityOrder.Popular
        : AvailabilityOrder.Cheapest;

    ingredient.availabilityOrder = availabilityOrder;
  }
}
</script>

<template>
  <AvailableAccommodationSegment
    v-if="isAvailableAccommodationSegment(props.segment)"
    :segment="props.segment"
    :ingredient-index="props.segment.context.ingredientIndex"
    :print-mode="props.printMode"
    @add-filter="onShowFilterPanel('from-plus')"
    @remove-filter="onRemoveFilter($event)"
    @show-details="onShowDetails"
    @show-more-filters="onShowFilterPanel('from-more')"
    @show-rooms="onShowRooms(false)"
    @toggle-accommodation="onToggleAccommodation($event)"
    @hotel-selection-navigation-triggered="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.AvailableAccommodation
      )
    "
  />

  <AlternativeRoomSuggestionAccommodationSegment
    v-if="isAlternativeRoomSuggestionAccommodationSegment(props.segment)"
    :segment="props.segment"
    @add-filter="onShowFilterPanel('from-plus')"
    @change-hotel="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.AlternativeAccommodation
      )
    "
    @confirm-alternative="confirmAlternative"
    @remove-filter="onRemoveFilter($event)"
    @show-details="onShowDetails"
    @show-more-filters="onShowFilterPanel('from-more')"
    @show-rooms="onShowRooms(true)"
    @hotel-selection-navigation-triggered="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.AlternativeAccommodation
      )
    "
  />

  <UnavailableAccommodationSegment
    v-if="isUnavailableAccommodationSegment(props.segment)"
    :segment="props.segment"
    :error-message="
      tripOverviewPageStore.getUnavailabilityMessage(
        props.segment.segmentType,
        props.segment.reasons
      )
    "
    @toggle-accommodation="onToggleAccommodation($event)"
    @add-filter="onShowFilterPanel('from-plus')"
    @edit-filters="
      pageConnectorStore.openHotelSelectionFilters(
        props.segment.context.ingredientIndex
      )
    "
    @explore-other-products="
      onHotelSelectionNavigationTriggered(
        ChangeHotelEventSource.UnavailableAccommodation
      )
    "
    @remove-filter="onRemoveFilter($event)"
    @show-more-filters="onShowFilterPanel('from-more')"
    @try-another-date="tripOverviewPageStore.scrollToCalendar()"
  />
</template>
